import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

interface HintItem {
  id: string;
  location: string;
  title: string;
  content: string;
  sortOrder: number;
}

interface HintState {
  lastSeenHintId: string | null;
  dismissedAt: string | null;
  dismissUntil: string | null;
}

interface HintsResponse {
  ok: boolean;
  hints: HintItem[];
  state: HintState | null;
}

function isDismissed(state: HintState | null): boolean {
  if (!state?.dismissedAt) return false;
  if (!state.dismissUntil) return true; // forever
  return new Date(state.dismissUntil) > new Date();
}

export function useHints(location: string) {
  const queryClient = useQueryClient();
  const [manualOpen, setManualOpen] = useState(false);
  const [manuallyClosed, setManuallyClosed] = useState(false);

  const query = useQuery({
    queryKey: ['hints', location],
    queryFn: async (): Promise<HintsResponse> => {
      const res = await fetch(`${API_URL}/hints?location=${encodeURIComponent(location)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch hints');
      return res.json();
    },
  });

  const hints = query.data?.hints ?? [];
  const state = query.data?.state ?? null;
  const dismissed = isDismissed(state);

  const initialIndex = useMemo(() => {
    if (!state?.lastSeenHintId || hints.length === 0) return 0;
    const idx = hints.findIndex((h) => h.id === state.lastSeenHintId);
    if (idx === -1) return 0;
    // Start at next unseen hint, or stay at last if all seen
    return Math.min(idx + 1, hints.length - 1);
  }, [hints, state]);

  const hasUnread = useMemo(() => {
    if (hints.length === 0) return false;
    if (!state?.lastSeenHintId) return true;
    const lastIdx = hints.findIndex((h) => h.id === state.lastSeenHintId);
    return lastIdx < hints.length - 1;
  }, [hints, state]);

  const isOpen = hints.length > 0 && (manualOpen || (!dismissed && hasUnread && !manuallyClosed));

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['hints', location] }).catch(() => {});

  const dismissMutation = useMutation({
    mutationFn: async (mode: 'today' | 'forever') => {
      const res = await fetch(`${API_URL}/hints/dismiss`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ location, mode }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to dismiss');
    },
    onSuccess: () => {
      setManualOpen(false);
      setManuallyClosed(true);
      invalidate();
    },
  });

  const seenMutation = useMutation({
    mutationFn: async (hintId: string) => {
      const res = await fetch(`${API_URL}/hints/seen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ location, hintId }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to mark seen');
    },
  });

  const open = useCallback(() => {
    setManualOpen(true);
    setManuallyClosed(false);
  }, []);

  const close = useCallback(() => {
    setManualOpen(false);
    setManuallyClosed(true);
  }, []);

  return {
    hints,
    state,
    initialIndex,
    isOpen,
    hasUnread,
    open,
    close,
    dismiss: dismissMutation.mutate,
    markSeen: seenMutation.mutate,
    isLoading: query.isLoading,
  };
}
